import { useTimelineContext } from 'dnd-timeline';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CarGroup } from '../grouping.js';
import type { ScheduleDensity } from '../scheduleDensity.js';
import type { ColumnAvailability } from './occupancy.js';

export interface GroupView extends CarGroup {
  /** 319 (US3): the group's own free cars per column (317's rule over this group's cars). */
  columns: ColumnAvailability[];
}

/**
 * 319 (US1, US2) — one group's heading: a button that names the group and its car count and
 * collapses or expands it (FR-006), across the sidebar. A plain row, NOT a `useRow` row, so it is
 * never a drop target or a place to draw (FR-008). Its height follows the row density (318).
 */
export function GroupHeader({
  mode,
  group,
  collapsed,
  onToggle,
  density = 'comfortable',
  children,
}: {
  mode: 'location' | 'category' | 'host';
  group: GroupView;
  collapsed: boolean;
  onToggle: (key: string) => void;
  density?: ScheduleDensity;
  /** The lane: the group's free cells (US3). */
  children?: React.ReactNode;
}) {
  const { sidebarWidth } = useTimelineContext();
  const count = `${group.cars.length} ${group.cars.length === 1 ? 'car' : 'cars'}`;
  const Chevron = collapsed ? ChevronRight : ChevronDown;
  return (
    <div
      data-testid={`schedule-group-${mode}-${group.key}`}
      data-collapsed={String(collapsed)}
      className={[
        'flex border-y border-hairline bg-surface-soft',
        density === 'compact' ? 'h-7' : 'h-9',
      ].join(' ')}
    >
      <div
        style={{ width: sidebarWidth }}
        className="sticky left-0 z-[5] flex shrink-0 items-center border-r border-hairline bg-surface-soft px-1"
      >
        <button
          type="button"
          aria-expanded={!collapsed}
          aria-label={`${group.name}, ${count}`}
          title={`${group.name}, ${count}`}
          onClick={() => onToggle(group.key)}
          className="flex min-w-0 items-center gap-1 rounded px-1 text-left text-xs font-semibold text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
        >
          <Chevron aria-hidden="true" size={14} className="shrink-0" />
          {/* The name is what matters: in Comfortable the count gets its own line so the name is
              not truncated by it; Compact keeps one line (full text in the title). */}
          <span
            className={[
              'flex min-w-0',
              density === 'compact' ? 'items-baseline gap-1' : 'flex-col leading-tight',
            ].join(' ')}
          >
            <span className="truncate">{group.name}</span>
            <span aria-hidden="true" className="shrink-0 text-[11px] font-normal text-steel">
              {density === 'compact' ? `· ${count}` : count}
            </span>
          </span>
        </button>
      </div>
      <div className="relative flex-1">{children}</div>
    </div>
  );
}
